import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { loadLocalSessions } from '../localTeam';
import type { LoadState } from '../loadState';

export interface SessionsRow {
  id: string;
  date: string;
  title: string | null;
  duration_mins: number | null;
  phase_coverage: Record<string, number> | null;
}

export type SessionsSource = 'live' | 'local';

interface UseTeamSessionsOptions {
  /** Inclusive lower bound on session.date. Defaults to 30 days ago. */
  since?: Date;
  /** Exclusive upper bound on session.date. Defaults to 14 days from now. */
  until?: Date;
  /** Order direction for the live query. 'desc' for list views, 'asc' for calendar grids. */
  order?: 'asc' | 'desc';
  /** Limit on the live query. Default 60. */
  limit?: number;
}

interface TeamSessionsResult {
  rows: SessionsRow[];
  state: LoadState;
  source: SessionsSource;
  errorMessage?: string;
}

/**
 * Shared session-fetcher used by Sessions list, Calendar week, and similar.
 * Handles the team-loading race, the Supabase live path, and the
 * localStorage fallback that picks up onboarding-week sessions + offline
 * Brain drafts. Distinguishes empty (filtered query had no rows) from
 * error (live + local both produced nothing useful).
 */
export function useTeamSessions(
  teamId: string | undefined,
  teamLoading: boolean,
  opts: UseTeamSessionsOptions = {},
): TeamSessionsResult {
  const order = opts.order ?? 'desc';
  const limit = opts.limit ?? 60;
  // Lock the default since/until on mount — using Date.now() inline here
  // re-runs on every render, busting the useEffect dep equality and
  // triggering an infinite refetch loop. Caller-provided dates are
  // already stable enough.
  const explicitSinceMs = opts.since?.getTime();
  const explicitUntilMs = opts.until?.getTime();
  const { sinceMs, untilMs } = useMemo(
    () => ({
      sinceMs: explicitSinceMs ?? Date.now() - 30 * 86_400_000,
      untilMs: explicitUntilMs ?? Date.now() + 14 * 86_400_000,
    }),
    [explicitSinceMs, explicitUntilMs],
  );

  const [result, setResult] = useState<TeamSessionsResult>({
    rows: [],
    state: 'loading',
    source: 'live',
  });

  useEffect(() => {
    if (teamLoading) {
      setResult({ rows: [], state: 'loading', source: 'live' });
      return;
    }
    if (!teamId) {
      setResult({ rows: [], state: 'empty', source: 'live' });
      return;
    }
    let cancelled = false;
    setResult({ rows: [], state: 'loading', source: 'live' });

    const since = new Date(sinceMs);
    const until = new Date(untilMs);

    const localFallback = (): SessionsRow[] =>
      loadLocalSessions()
        .filter((s) => s.team_id === teamId)
        .filter((s) => {
          const t = new Date(s.date).getTime();
          return t >= sinceMs && t < untilMs;
        })
        .sort((a, b) =>
          order === 'asc'
            ? +new Date(a.date) - +new Date(b.date)
            : +new Date(b.date) - +new Date(a.date),
        )
        .map((s) => ({
          id: s.id,
          date: s.date,
          title: s.title,
          duration_mins: s.duration_mins,
          phase_coverage: s.phase_coverage,
        }));

    (async () => {
      let liveError: string | undefined;
      try {
        const { data, error } = await supabase
          .from('sessions')
          .select('id, date, title, duration_mins, phase_coverage')
          .eq('team_id', teamId)
          .is('deleted_at', null)
          .gte('date', since.toISOString())
          .lt('date', until.toISOString())
          .order('date', { ascending: order === 'asc' })
          .limit(limit);
        if (cancelled) return;
        if (error) {
          liveError = error.message;
        } else {
          const rows = (data ?? []) as SessionsRow[];
          if (rows.length > 0) {
            setResult({ rows, state: 'ok', source: 'live' });
            return;
          }
        }
      } catch (err) {
        if (cancelled) return;
        liveError = err instanceof Error ? err.message : 'fetch failed';
      }

      // Live path returned 0 rows or threw — fall back to localStorage.
      const local = localFallback();
      if (local.length > 0) {
        setResult({ rows: local, state: 'ok', source: 'local' });
        return;
      }
      // Nothing live, nothing local. If the live path actually errored,
      // surface that; otherwise it's a true empty state.
      setResult(
        liveError
          ? { rows: [], state: 'error', source: 'live', errorMessage: liveError }
          : { rows: [], state: 'empty', source: 'live' },
      );
    })();
    return () => { cancelled = true; };
  }, [teamId, teamLoading, sinceMs, untilMs, order, limit]);

  return result;
}
